import React from 'react';
import { Alert, Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import { EmailTrackingMode, EmailTrackingSettings } from '../../types';

interface Props {
  settings: EmailTrackingSettings;
  saving: boolean;
  onChoose: (mode: EmailTrackingMode) => void;
}

interface Option {
  mode: 'alias' | 'copy';
  title: string;
  how: string;
  rows: [string, string][];
  risk: string;
}

const OPTIONS: Option[] = [
  {
    mode: 'alias',
    title: 'Apply with a JobFlo address',
    how: 'The extension fills your private JobFlo address into application forms. JobFlo reads each employer email and forwards it to your own inbox.',
    rows: [
      ['Setup', 'None.'],
      ['What is tracked', 'Everything employers send for applications made with the address.'],
      ['If JobFlo is down', 'Mail waits and arrives late. It is not lost.'],
      ['Who sees the mail', 'JobFlo handles every employer email for those applications.'],
      ['Replying', 'From your own address, which the employer then sees.'],
    ],
    risk: 'JobFlo sits between the employer and your inbox.',
  },
  {
    mode: 'copy',
    title: 'Forward a copy from your own mailbox',
    how: 'You keep applying with your own email. One forwarding rule in your mailbox sends JobFlo a copy of employer mail.',
    rows: [
      ['Setup', 'Once, about three steps in your mailbox settings.'],
      ['What is tracked', 'Mail your filter matches: the automated mail hiring systems send. A recruiter writing from a personal address is missed unless you widen the filter.'],
      ['If JobFlo is down', 'Nothing happens to your mail. Tracking pauses and catches up.'],
      ['Who sees the mail', 'JobFlo sees only what your filter forwards.'],
      ['Replying', 'Unchanged.'],
    ],
    risk: 'Some mail can be missed.',
  },
];

/** The two ways mail can reach JobFlo, side by side, and switching the feature off. */
const ModeChooser: React.FC<Props> = ({ settings, saving, onChoose }) => (
  <Stack spacing={2}>
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2, alignItems: 'stretch' }}>
      {OPTIONS.map((option) => {
        const current = settings.mode === option.mode;
        const unavailable = option.mode === 'alias' && !settings.alias_available;
        return (
          <Paper
            key={option.mode}
            variant="outlined"
            component="section"
            aria-label={option.title}
            sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.25, borderColor: current ? 'primary.main' : 'divider', borderWidth: current ? 2 : 1 }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
              <Typography variant="h6" component="h3" sx={{ fontWeight: 700 }}>
                {option.title}
              </Typography>
              {option.mode === 'alias' && <Chip size="small" color="primary" label="Recommended" />}
              {current && <Chip size="small" color="success" variant="outlined" label="In use" />}
            </Stack>
            <Typography variant="body2">{option.how}</Typography>
            <Box component="dl" sx={{ m: 0, display: 'grid', gap: 1 }}>
              {option.rows.map(([name, text]) => (
                <Box key={name}>
                  <Typography component="dt" variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {name}
                  </Typography>
                  <Typography component="dd" variant="body2" sx={{ m: 0 }}>
                    {text}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Typography variant="body2" color="text.secondary">
              The one risk: {option.risk}
            </Typography>
            <Box sx={{ flex: 1 }} />
            {unavailable ? (
              <Alert severity="info" sx={{ py: 0 }}>
                Not available yet: JobFlo is waiting for approval to forward mail to any inbox. You can start with a copy
                and switch later.
              </Alert>
            ) : option.mode === 'alias' && !settings.email_confirmed && !current ? (
              <Alert severity="info" sx={{ py: 0 }}>
                Confirm your email first: sign out and sign in with Google using {settings.forwards_to}. JobFlo only
                forwards mail to an address its owner has confirmed.
              </Alert>
            ) : (
              <Button variant={current ? 'outlined' : 'contained'} disabled={saving || current} onClick={() => onChoose(option.mode)}>
                {current ? 'In use' : settings.mode === 'off' ? 'Use this' : 'Switch to this'}
              </Button>
            )}
          </Paper>
        );
      })}
    </Box>
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 240 }}>
        You can switch at any time. Your JobFlo address never changes, and mail sent to it is always forwarded to you,
        whichever option is in use.
      </Typography>
      {settings.mode !== 'off' && (
        <Button color="inherit" disabled={saving} onClick={() => onChoose('off')}>
          Turn tracking off
        </Button>
      )}
    </Stack>
  </Stack>
);

export default ModeChooser;
